import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Title, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Badge, Text } from '@tremor/react';
import { FilterBar, buildQueryString, useFilters } from './Filters';
import { useApi } from './useApi';

type ResponseRow = {
  response_id: string;
  recipient_id: string;
  member_id: string;
  survey_key: string;
  dock_name: string | null;
  completed_at: string | null;
  nps_score: number | null;
  sentiment: string | null;
  flagged: boolean;
  flag_reason: string | null;
  first_name: string | null;
  last_name: string | null;
};

type ResponsesPayload = {
  total: number;
  limit: number;
  offset: number;
  rows: ResponseRow[];
};

const PAGE_SIZE = 50;

export function ResponsesPage(): JSX.Element {
  const [filters, setFilters] = useFilters();
  const [offset, setOffset] = useState(0);
  const [flagged, setFlagged] = useState<'all' | 'true' | 'false'>('all');
  const navigate = useNavigate();

  const queryParams = buildQueryString(filters);
  const flaggedParam = flagged === 'all' ? '' : `&flagged=${flagged}`;
  const url = `/api/admin/metrics/responses?${queryParams}&limit=${PAGE_SIZE}&offset=${offset}${flaggedParam}`;
  const data = useApi<ResponsesPayload>(url, [JSON.stringify(filters), offset, flagged]);

  const onCsvExport = (): void => {
    const csvUrl = `/api/admin/export/responses.csv?${queryParams}`;
    window.location.href = csvUrl;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Title>Responses</Title>
      </div>

      <FilterBar
        value={filters}
        onChange={(v) => { setFilters(v); setOffset(0); }}
        docks={[]}
        rightSlot={
          <div className="flex items-center gap-3">
            <select
              value={flagged}
              onChange={(e) => { setFlagged(e.target.value as 'all' | 'true' | 'false'); setOffset(0); }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="all">All responses</option>
              <option value="true">Flagged only</option>
              <option value="false">Not flagged</option>
            </select>
            <button
              type="button"
              onClick={onCsvExport}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded"
            >
              Export CSV
            </button>
          </div>
        }
      />

      <Card>
        {data.kind === 'loading' && <Text>Loading…</Text>}
        {data.kind === 'error' && <Text>Couldn't load responses ({data.message})</Text>}
        {data.kind === 'ready' && (
          <>
            <div className="text-sm text-gray-600 mb-3">
              {data.data.total.toLocaleString()} total — page {Math.floor(offset / PAGE_SIZE) + 1} of {Math.max(1, Math.ceil(data.data.total / PAGE_SIZE))}
            </div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Completed</TableHeaderCell>
                  <TableHeaderCell>Survey</TableHeaderCell>
                  <TableHeaderCell>Member</TableHeaderCell>
                  <TableHeaderCell>Dock</TableHeaderCell>
                  <TableHeaderCell className="text-right">NPS</TableHeaderCell>
                  <TableHeaderCell>Sentiment</TableHeaderCell>
                  <TableHeaderCell>Flag</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.data.rows.map((row) => (
                  <TableRow
                    key={row.response_id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/admin/responses/${row.response_id}`)}
                  >
                    <TableCell className="whitespace-nowrap">{formatDate(row.completed_at)}</TableCell>
                    <TableCell>{surveyLabel(row.survey_key)}</TableCell>
                    <TableCell>{[row.first_name, row.last_name].filter(Boolean).join(' ') || '(unknown)'}</TableCell>
                    <TableCell>{row.dock_name ?? '—'}</TableCell>
                    <TableCell className="text-right">{row.nps_score ?? '—'}</TableCell>
                    <TableCell>{row.sentiment ? <Badge color={sentimentColor(row.sentiment)}>{row.sentiment}</Badge> : '—'}</TableCell>
                    <TableCell>{row.flagged ? <Badge color="red">{row.flag_reason ?? 'flagged'}</Badge> : '—'}</TableCell>
                  </TableRow>
                ))}
                {data.data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={7}><Text>No responses match these filters.</Text></TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded disabled:opacity-50"
              >Prev</button>
              <button
                type="button"
                disabled={offset + PAGE_SIZE >= data.data.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded disabled:opacity-50"
              >Next</button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
         d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function surveyLabel(key: string): string {
  return ({ post_sale: 'Post-sale', post_trip: 'Post-trip', post_training: 'Post-training' } as Record<string, string>)[key] ?? key;
}
function sentimentColor(s: string): 'emerald' | 'gray' | 'red' | 'amber' {
  if (s === 'positive') return 'emerald';
  if (s === 'negative') return 'red';
  if (s === 'mixed')    return 'amber';
  return 'gray';
}
